import { useMemo } from 'react';

import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Text, TextType } from 'ui-components/components/text';
import { GuidService } from 'ui-components/services/guid-service';

import style from './dropdown-field.module.less';
import { DropdownInlineMessage } from './dropdown-inline-message';
import { ReactSelectDropdown } from './react-select-wrapper/react-select-dropdown';

/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOption} DropdownOption
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOptionGroup}
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownInlineMessageType} DropdownInlineMessageType
 */
/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOnChange} DropdownOnChange
 */
/**
 * @typedef {import('../../components/text/text-types.d.ts').TextValue} TextValue
 */

/**
 * @param {object} props
 * @param {string} [props.ariaLabel]
 * @param {string} [props.className]
 * @param {boolean} [props.isMenuFullWidth]
 * @param {(option: DropdownOption) => boolean} [props.getOptionIsDisabled] - Defaults to `option.disabled`
 * @param {(option: DropdownOption, selectedOptions: DropdownOption[]) => boolean} [props.getOptionIsSelected] - This is typically not necessary unless there are multiple values used to determine if an option is selected. This should be used in conjunction with `selectedOption` instead of `selectedItemValue`
 * @param {(option: DropdownOption) => string} [props.getOptionLabel] - Defaults to `option.label`
 * @param {(option: DropdownOption) => string} [props.getOptionValue] - Defaults to `option.value`
 * @param {string} [props.id]
 * @param {React.ReactNode} [props.inlineMessage]
 * @param {DropdownInlineMessageType} [props.inlineMessageType]
 * @param {boolean} [props.isClearable]
 * @param {boolean} [props.isDisabled]
 * @param {boolean} [props.isSearchable]
 * @param {TextValue} [props.label]
 * @param {boolean} [props.required]
 * @param {boolean} [props.isRequiredError]
 * @param {DropdownOnChange} props.onItemSelected
 * @param {DropdownOptionGroup[]} [props.optionGroups]
 * @param {DropdownOption[]} [props.options]
 * @param {string} [props.placeholder]
 * @param {(option: DropdownOption) => ReactNode} [props.renderOption]
 * @param {(option: DropdownOption) => ReactNode} [props.renderSelectedValue]
 * @param {string} props.selectedItemValue
 * @param {DropdownOption} [props.selectedOption] - `selectedItemValue` should typically be preferred over this. `selectedOption` is only necessary when the selected option is not in the list of options or when multiple properties are needed to determine if an option is selected
 * @param {boolean} [props.shouldTruncateGroupHeader]
 * @returns {JSX.Element}
 */
export const DropdownField = ({
  ariaLabel,
  className,
  isMenuFullWidth,
  getOptionIsDisabled,
  getOptionIsSelected,
  getOptionLabel,
  getOptionValue,
  id,
  inlineMessage,
  isRequiredError = false,
  inlineMessageType,
  isClearable = false,
  isDisabled,
  isSearchable = false,
  label,
  onItemSelected,
  optionGroups,
  options,
  placeholder,
  required = false,
  renderOption,
  renderSelectedValue,
  selectedItemValue = '',
  selectedOption,
  shouldTruncateGroupHeader = false,
}) => {
  const hasError = (inlineMessageType === INLINE_MESSAGE_TYPES.error && inlineMessage) || isRequiredError;

  const dropdownId = useMemo(() => {
    return id || GuidService.newGuid();
  }, [id]);

  return (
    <div className={style['dropdown-field']}>
      {label ? <Text htmlFor={dropdownId} textType={TextType.Label} value={label} /> : null}
      <ReactSelectDropdown
        ariaLabel={ariaLabel}
        className={className}
        isMenuFullWidth={isMenuFullWidth}
        getOptionIsDisabled={getOptionIsDisabled}
        getOptionIsSelected={getOptionIsSelected}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        hasError={hasError}
        inputId={dropdownId}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        onItemSelected={onItemSelected}
        optionGroups={optionGroups}
        options={options}
        placeholder={placeholder}
        renderOption={renderOption}
        renderSelectedValue={renderSelectedValue}
        required={required}
        selectedItemValue={selectedItemValue}
        selectedOption={selectedOption}
        shouldTruncateGroupHeader={shouldTruncateGroupHeader}
      />
      {inlineMessage ? <DropdownInlineMessage inlineMessage={inlineMessage} inlineMessageType={inlineMessageType} /> : null}
    </div>
  );
};
